import React, {useEffect, useState} from "react";
import {initialUploadFormData} from "./AddEditModal";
import TextField from "@mui/material/TextField";
import {FilesForm} from "./FilesForm";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import {DialogActions, DialogContent} from "@mui/material";
import {ErrorMessage} from "../../common/ErrorMessage";
import {Stage, UploadStage} from "../../../api/stage/getStages";
import {useMutation} from "@apollo/client";
import {UPDATE_STAGE} from "../../../api/stage/updateStage";
import {CREATE_UPLOAD_STAGE} from "../../../api/stage/createUploadStage";

type UploadFormProps = {
    formData: UploadStage,
    onClose: (shouldRefetch: boolean) => void,
    setLoading: (nextValue: boolean) => void,
    updateFormData: (newValues: Partial<typeof initialUploadFormData>) => void,
}

export const UploadForm = (props: UploadFormProps) => {
    const {formData, onClose, setLoading, updateFormData} = props

    const [snackBarMessage, setSnackBarMessage] = useState('')

    const [createUploadStage, {error: createError}] = useMutation(CREATE_UPLOAD_STAGE)
    const [updateStage, {error: updateError}] = useMutation(UPDATE_STAGE)

    const onSubmit = async () => {
        setLoading(true)

        if (formData.id === '') {
            const {errors} = await createUploadStage({variables: {input: {
                title: formData.title,
                description: formData.description,
                files: formData.files,
            }}})

            if (errors === undefined || errors.length === 0) {
                setSnackBarMessage('')
                onClose(true)
            }
        } else {
            const {errors} = await updateStage({variables: {input: {
                description: formData.description,
                files: formData.files,
                id: formData.id,
                title: formData.title,
                type: formData.type,
            }}})

            if (errors === undefined || errors.length === 0) {
                setSnackBarMessage('')
                onClose(true)
            }
        }

        setLoading(false)
    }

    useEffect(() => {
        if (createError) setSnackBarMessage(createError.message)
        else if (updateError) setSnackBarMessage(updateError.message)
    }, [createError?.message, updateError?.message])

    return (
        <>
            <DialogContent>
                <Stack gap="1.5rem">
                    <TextField
                        label="Title"
                        onChange={(e) => updateFormData({title: e.target.value})}
                        value={formData.title}
                        variant="outlined"
                    />

                    <TextField
                        label="Description"
                        minRows={3}
                        multiline
                        onChange={(e) => updateFormData({description: e.target.value})}
                        value={formData.description}
                        variant="outlined"
                    />

                    <FilesForm
                        formData={formData}
                        updateFormData={(newStage: Stage) => updateFormData(newStage)}
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={() => onClose(false)}
                    variant="contained"
                    color="error"
                >
                    Close
                </Button>
                <Button
                    color="success"
                    disabled={
                        formData.title === ''
                        || formData.description === ''
                        || formData.rejectionMessage === ''
                        || formData.files.some(file => file.title === '')
                    }
                    onClick={onSubmit}
                    variant="contained"
                >
                    {formData.id === '' ? 'Add' : 'Edit'}
                </Button>
            </DialogActions>

            <ErrorMessage
                handleClose={() => setSnackBarMessage('')}
                isOpen={snackBarMessage !== ''}
                message={snackBarMessage}
            />
        </>
    )
}
